import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChatsCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Strips a "(N) " prefix so we never stack badges on top of each other.
const stripBadge = (t) => t.replace(/^\(\d+\+?\)\s/, "");

/**
 * Inline DM nav button with live unread badge.
 * Polls /chat/unread-total every 20s (also while the tab is in the background,
 * so the browser-tab title badge stays fresh) and mirrors the count into
 * document.title as "(N) hacking.community".
 */
export default function DMNavButton() {
    const { user } = useAuth();
    const location = useLocation();
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        const fetchUnread = async () => {
            try {
                const { data } = await api.get("/chat/unread-total");
                if (!cancelled) setUnread(data.total || 0);
            } catch { /* noop */ }
        };
        fetchUnread();
        const id = setInterval(fetchUnread, 20000);
        const onVis = () => { if (!document.hidden) fetchUnread(); };
        document.addEventListener("visibilitychange", onVis);
        return () => { cancelled = true; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    }, [user, location.pathname]);

    // Browser-tab title badge — survives react-helmet title swaps on navigation
    // via a MutationObserver on the <title> node.
    useEffect(() => {
        const apply = () => {
            const base = stripBadge(document.title);
            const want = unread > 0 ? `(${unread > 99 ? "99+" : unread}) ${base}` : base;
            if (document.title !== want) document.title = want;
        };
        apply();
        const titleEl = document.querySelector("title");
        if (!titleEl) return undefined;
        const mo = new MutationObserver(apply);
        mo.observe(titleEl, { childList: true, characterData: true, subtree: true });
        return () => {
            mo.disconnect();
            document.title = stripBadge(document.title);
        };
    }, [unread]);

    if (!user) return null;
    return (
        <Link to="/chat" data-testid="nav-chat" className="relative flex items-center justify-center w-11 h-11 sm:w-9 sm:h-9 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-white/5 transition-colors" title="Encrypted messages">
            <ChatsCircle size={18} weight="duotone" />
            {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-cyan-400 text-slate-950 text-[9px] font-bold leading-none px-1.5 py-0.5 rounded-sm" data-testid="nav-chat-unread">
                    {unread > 99 ? "99+" : unread}
                </span>
            )}
        </Link>
    );
}
